import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";
import { vibrantTheme } from "../components/common";
import { GUIDES } from "../content/guides";
import { SITE } from "../lib/site";
import TranslateAndListen from "../components/TranslateAndListen";
import TripAutoHero from "../components/TripAutoHero";

const ORDER = ["air", "rail", "bus", "road", "water", "stay", "ground", "rideshare", "deals"];

export default function GuidesIndex() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="guides-index-page">
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="global travel modes airplane train boat" alt="Travel guides"
          className="absolute inset-0 w-full h-full" testid="guides-index-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/90 via-primary/70 to-cyan-600/55" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-14">
          <p className="text-xs font-mono uppercase tracking-widest text-white/85">{SITE.name} · Travel Guides</p>
          <h1 className="mt-2 font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white">The Travel Guide Library</h1>
          <p className="mt-3 text-base text-white/90 max-w-3xl">Nine in-depth guides covering every way the world moves — flights, trains, buses, roads, water, stays and more.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex justify-end mb-4">
          <TranslateAndListen />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ORDER.filter((m) => GUIDES[m]).map((mode, i) => {
            const g = GUIDES[mode];
            const theme = vibrantTheme(`guide-${mode}`);
            return (
              <Link key={mode} to={`/guides/${mode}`} data-testid={`guide-card-${mode}`}
                className="group border border-zinc-300 bg-white hover:shadow-xl transition-all flex flex-col"
                style={{ borderTopWidth: 4, borderTopColor: theme.bg }}>
                <div className="p-6 flex-1">
                  <div className="text-3xl mb-3">{g.emoji}</div>
                  <h2 className="font-heading font-900 text-xl text-zinc-900 group-hover:text-primary transition-colors">{g.title}</h2>
                  <div className="text-sm font-700 mt-1" style={{ color: theme.bg }}>{g.subtitle}</div>
                  <p className="text-sm text-zinc-600 mt-3 line-clamp-3">{g.teaser?.[0]}</p>
                </div>
                <div className="px-6 pb-5 flex items-center justify-between">
                  <span className="text-sm font-700 inline-flex items-center gap-1.5" style={{ color: theme.bg }}>
                    Read the guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}/09</span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          <Link to="/explore-plan-share-travel" data-testid="guide-card-our-story"
            className="group text-white p-8 flex items-center justify-between hover:opacity-95 transition-opacity"
            style={{ backgroundColor: vibrantTheme("guides-our-story").bg }}>
            <div>
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/80 mb-2">
                <Sparkles className="w-4 h-4" /> Our Story
              </div>
              <div className="font-heading font-900 text-2xl">Explore &bull; Plan &bull; Share &bull; Travel</div>
              <p className="text-sm text-white/85 mt-2">How TravelerPulse.pro connects the global travel community.</p>
            </div>
            <ArrowRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/visas" data-testid="guide-card-visas"
            className="group text-white p-8 flex items-center justify-between hover:opacity-95 transition-opacity"
            style={{ backgroundColor: vibrantTheme("guides-visas").bg }}>
            <div>
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/80 mb-2">
                <BookOpen className="w-4 h-4" /> Visas &amp; Embassies
              </div>
              <div className="font-heading font-900 text-2xl">Country Visa Guides</div>
              <p className="text-sm text-white/85 mt-2">Entry requirements, embassies and consulates for destinations worldwide.</p>
            </div>
            <ArrowRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}
